import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, Shield, Heart, User, ArrowLeft } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { 
  getWebIDByIdPublic, 
  updateEmergencyProfile, 
  completeWebIDOnboarding 
} from '../../services/webids';
import EmergencyProfileFormUnified, { EmergencyProfileFormData } from '../ui/EmergencyProfileFormUnified';
import LoadingSpinner from '../ui/LoadingSpinner';
import { Button } from '../ui';

/**
 * @component WebIDOnboarding
 * @description Componente de onboarding para configurar el perfil de emergencia
 * Usa el formulario unificado y maneja aspectos legales ARCO
 */

interface OnboardingState {
  step: 'loading' | 'form' | 'completing' | 'completed' | 'error';
  error?: string;
  webIdData?: any;
}

const WebIDOnboarding: React.FC = () => {
  const { webId } = useParams<{ webId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [state, setState] = useState<OnboardingState>({
    step: 'loading'
  });

  // Verificar acceso y cargar datos
  useEffect(() => {
    const loadWebIDData = async () => {
      if (!webId || !user) {
        setState({ step: 'error', error: 'Acceso no autorizado' });
        return;
      }

      try {
        const webIdData = await getWebIDByIdPublic(webId);
        
        // Verificar que el usuario tenga acceso
        if (webIdData.userId !== user.uid) {
          setState({ step: 'error', error: 'No tienes acceso a este perfil' });
          return;
        }

        // Verificar que esté en estado correcto para onboarding
        if (webIdData.activationStatus !== 'activated') {
          setState({ step: 'error', error: 'Este perfil no está listo para configuración' });
          return;
        }

        setState({ step: 'form', webIdData });

      } catch (error) {
        console.error('Error cargando datos del WebID:', error);
        setState({ step: 'error', error: 'Error al cargar el perfil de emergencia' });
      }
    };

    loadWebIDData();
  }, [webId, user]);

  // Manejar envío del formulario
  const handleFormSubmit = async (formData: EmergencyProfileFormData) => {
    if (!webId || !user) return;

    try {
      setState(prev => ({ ...prev, step: 'completing' }));

      // Mapear contactos de emergencia con IDs únicos
      const mappedContacts = formData.emergencyContacts.map((contact, index) => ({
        id: `contact_${Date.now()}_${index}`,
        name: contact.name,
        relationship: contact.relationship,
        phone: contact.phone,
        email: contact.email || '',
        isPrimary: contact.isPrimary,
        canReceiveAlerts: true,
        notes: '',
      }));

      // Convertir el primer contacto a formato string para compatibilidad
      const primaryContact = mappedContacts.find(c => c.isPrimary) || mappedContacts[0];
      const emergencyContactString = primaryContact
        ? `${primaryContact.name} - ${primaryContact.relationship} - ${primaryContact.phone}`
        : '';

      // Actualizar perfil de emergencia
      await updateEmergencyProfile(webId, {
        fullName: formData.name, // Corregir: usar fullName
        phone: formData.phone ? `${formData.phoneCountryCode} ${formData.phone}` : '',
        emergencyContacts: mappedContacts, // Enviar array completo de contactos
        bloodType: formData.bloodType,
        medicalConditions: formData.diseases, // Corregir: usar medicalConditions
        allergies: formData.allergies,
        medications: formData.medications,
        medicalNotes: formData.medicalNotes || formData.medicalInfo || '', // Corregir: usar medicalNotes
        isPublic: formData.isPublic,
        // Agregar campos de privacidad granular
        showAllergies: formData.showAllergies,
        showMedicalConditions: formData.showMedicalConditions,
        showMedications: formData.showMedications,
        showMedicalNotes: formData.showMedicalNotes,
        showEmergencyContacts: formData.showEmergencyContacts,
        allowEmergencyAccess: formData.allowEmergencyAccess,
        language: 'es',
        timezone: 'America/Santiago',
      }, user.uid);

      // Completar onboarding
      await completeWebIDOnboarding(webId, user.uid);

      setState({ step: 'completed' });

      // Redirigir al dashboard después de 3 segundos
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (error: any) {
      console.error('Error completando onboarding:', error);
      setState(prev => ({ 
        ...prev, 
        step: 'form', 
        error: error.message || 'Error al guardar el perfil de emergencia' 
      }));
    }
  };

  // Renderizar según el estado
  const renderContent = () => {
    switch (state.step) {
      case 'loading':
        return (
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" message="Cargando configuración..." />
          </div>
        );

      case 'form':
        return (
          <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
              <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      Configurar Perfil de Emergencia
                    </h1>
                    <p className="text-gray-600 mt-1">
                      Completa tu información médica para situaciones de emergencia
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="bg-white border-b">
              <div className="max-w-4xl mx-auto px-4 py-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">Cuenta Creada</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                      <Heart className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-sm text-primary-600 font-medium">Configurar Perfil</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                      <Shield className="h-3 w-3 text-gray-400" />
                    </div>
                    <span className="text-sm text-gray-400">Completado</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6">
                  {/* Legal Notice */}
                  <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-4">
                      <Shield className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-blue-900 mb-2">
                          Protección de Datos Personales - Ley N° 19.628
                        </h3>
                        <div className="text-blue-800 text-sm space-y-2">
                          <p>
                            <strong>Finalidad:</strong> Los datos que proporciones se utilizarán exclusivamente 
                            para brindar información médica vital en situaciones de emergencia.
                          </p>
                          <p>
                            <strong>Derechos ARCO:</strong> Tienes derecho a Acceder, Rectificar, Cancelar y 
                            Oponerte al tratamiento de tus datos personales en cualquier momento.
                          </p>
                          <p>
                            <strong>Responsable:</strong> NFCores es el responsable del tratamiento de tus datos 
                            y garantiza su protección según la legislación chilena.
                          </p>
                          <p>
                            <strong>Contacto:</strong> Para ejercer tus derechos o consultas sobre privacidad, 
                            escribe a <a href="mailto:privacy@nfcores.cl" className="underline">privacy@nfcores.cl</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form */}
                  <EmergencyProfileFormUnified
                    mode="create"
                    onSubmit={handleFormSubmit}
                    loading={false}
                    error={state.error}
                    showPrivacySettings={true}
                    className="space-y-8"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'completing':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-blue-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full mx-4">
              <LoadingSpinner size="lg" message="Guardando tu perfil de emergencia..." />
              <p className="text-gray-600 text-sm mt-4">
                Esto puede tomar unos segundos...
              </p>
            </div>
          </div>
        );

      case 'completed':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full mx-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                ¡Perfil Completado!
              </h1>
              
              <p className="text-gray-600 mb-6">
                Tu perfil de emergencia ha sido configurado exitosamente. 
                Ahora los servicios de emergencia podrán acceder a tu información médica cuando sea necesario.
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-primary-600 hover:bg-primary-700"
                >
                  Ir a Mi Dashboard
                </Button>
                
                <Button
                  onClick={() => navigate(`/profile/${webId}`)}
                  variant="outline"
                  className="w-full"
                >
                  Ver Mi Perfil Público
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                Serás redirigido automáticamente en unos segundos...
              </p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-md w-full mx-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Error de Configuración
              </h1>
              
              <p className="text-gray-600 mb-4">
                {state.error}
              </p>
              
              <div className="space-y-2">
                <Button
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Intentar de Nuevo
                </Button>
                
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full"
                >
                  Ir al Dashboard
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderContent();
};

export default WebIDOnboarding;
